/* Generated from Java with JSweet 2.3.7 - http://www.jsweet.org */
import { CombinedAllNodeVisitor } from '../../combined/visitors/CombinedAllNodeVisitor';
import { NodeCompat } from '../../compat/NodeCompat';
import { RegexCompat } from '../../compat/RegexCompat';
import { Node } from '../models/Node';
import { IVisitor } from '../visitors/IVisitor';

/**
 * Constructor.
 * @param {ValidationRuleMetaData} ruleInfo
 * @class
 * @extends CombinedAllNodeVisitor
 * @author eric.wittmann@gmail.com
 */
export abstract class ValidationRule extends CombinedAllNodeVisitor implements IVisitor {
    /**
     * List of valid HTTP response status codes from:  https://www.iana.org/assignments/http-status-codes/http-status-codes.xhtml
     */
    static HTTP_STATUS_CODES : Array<string> = null;

    static PATH_MATCH_REGEX : string = "^(\\/[^{}\\/]*(\\{[a-zA-Z_][0-9a-zA-Z_]*\\})?)+$";

    static SEG_MATCH_REGEX : string = "\\/([^{}\\/]*)(\\{([a-zA-Z_][0-9a-zA-Z_]*)\\})?";

    static URL_MATCH_REGEX : string = "^(?!mailto:)(?:(?:http|https|ftp)://)(?:\\S+(?::\\S*)?@)?(?:(?:(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}(?:\\.(?:[0-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))|(?:(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)(?:\\.(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)*(?:\\.(?:[a-z\\u00a1-\\uffff]{2,})))|localhost)(?::\\d{2,5})?(?:(/|\\?|#)[^\\s]*)?$";

    static EMAIL_MATCH_REGEX : string = "^[\\w!#$%&\'*+/=?`{|}~^-]+(?:\\.[\\w!#$%&\'*+/=?`{|}~^-]+)*@(?:[a-zA-Z0-9-]+\\.)+[a-zA-Z]{2,6}$";

    static MIME_TYPE_MATCH_REGEX : string = "^.*\\/.*(;.*)?$";

    /*private*/ reporter : IValidationProblemReporter;

    /*private*/ ruleInfo : ValidationRuleMetaData;

    public constructor(ruleInfo : ValidationRuleMetaData) {
        super();
        (() => {
            ValidationRule.HTTP_STATUS_CODES = <any>(/* asList */["100", "101", "102", "1XX", "10X", "200", "201", "202", "203", "204", "205", "206", "207", "208", "226", "2XX", "20X", "21X", "22X", "300", "301", "302", "303", "304", "305", "306", "307", "308", "3XX", "30X", "400", "401", "402", "403", "404", "405", "406", "407", "408", "409", "410", "411", "412", "413", "414", "415", "416", "417", "4XX", "40X", "41X", "421", "422", "423", "424", "426", "427", "428", "429", "431", "451", "42X", "43X", "44X", "45X", "500", "501", "502", "503", "504", "505", "506", "507", "508", "510", "511", "5XX", "50X", "51X"].slice(0).slice(0));
        })();
        if(this.reporter===undefined) this.reporter = null;
        if(this.ruleInfo===undefined) this.ruleInfo = null;
        this.ruleInfo = ruleInfo;
    }

    /**
     * Sets the validation problem reporter.
     * @param {*} reporter
     */
    public setReporter(reporter : IValidationProblemReporter) {
        this.reporter = reporter;
    }

    /**
     * Called by validation rules to report an error.
     * @param {Node} node
     * @param {string} property
     * @param {*} messageParams
     */
    report(node : Node, property : string, messageParams : any) {
        let messageTemplate : string = this.ruleInfo.messageTemplate;
        let message : string = this.resolveMessage(messageTemplate, messageParams);
        this.reporter.report(this.ruleInfo, node, property, message);
    }

    /**
     * Creates a message from a template and set of params.
     * @param {string} messageTemplate
     * @param {*} messageParams
     * @return {string}
     * @private
     */
    resolveMessage(messageTemplate : string, messageParams : any) : string {
        if(messageParams == null || /* size */Object.keys(messageParams).length === 0) {
            return messageTemplate;
        }
        let matches : Array<string[]> = RegexCompat.findMatches(messageTemplate, "\\$\\{\\s*\'\\s*([a-zA-Z0-9]+)\\s*\'\\s*\\}");
        if(/* size */(<number>matches.length) === 0) {
            return messageTemplate;
        }
        let rval : string = messageTemplate;
        for(let index132=0; index132 < matches.length; index132++) {
            let match = matches[index132];
            {
                let mval : string = match[0];
                let paramName : string = match[1];
                let paramValue : string = /* get */((m,k) => m[k]===undefined?null:m[k])(messageParams, paramName);
                let start : number = rval.indexOf(mval);
                let end : number = start + mval.length;
                rval = rval.substring(0, start) + paramValue + rval.substring(end);
            }
        }
        return rval;
    }

    /**
     * Reports a validation error if the property is not valid.
     * @param {boolean} isValid
     * @param {Node} node
     * @param {string} property
     * @param {*} messageParams
     */
    reportIfInvalid(isValid : boolean, node : Node, property : string, messageParams : any) {
        if(!isValid) {
            this.report(node, property, messageParams);
        }
    }

    /**
     * Reports a validation error if the given condition is true.
     * @param {boolean} condition
     * @param {Node} node
     * @param {string} property
     * @param {*} messageParams
     */
    reportIf(condition : boolean, node : Node, property : string, messageParams : any) {
        if(condition) {
            this.report(node, property, messageParams);
        }
    }

    /**
     * Utility function to report path related errors.
     * @param {Node} node
     * @param {*} messageParams
     */
    reportPathError(node : Node, messageParams : any) {
        this.report(node, null, messageParams);
    }

    /**
     * Check if a property was defined.
     * @param {*} propertyValue
     * @return {boolean} {boolean}
     */
    isDefined(propertyValue : any) : boolean {
        return !NodeCompat.isNullOrUndefined(propertyValue);
    }

    /**
     * Check if the property value exists (is not undefined and is not null).
     * @param {*} propertyValue
     * @return {boolean} {boolean}
     */
    hasValue(propertyValue : any) : boolean {
        return this.isDefined(propertyValue);
    }

    /**
     * Checks the path template against the regular expression and returns match result.
     * 
     * @param {string} pathTemplate
     * @return {boolean} {boolean}
     */
    isPathWellFormed(pathTemplate : string) : boolean {
        return RegexCompat.matches(pathTemplate, ValidationRule.PATH_MATCH_REGEX);
    }

    /**
     * Finds all occurences of path segment patterns in a path template.
     * 
     * @param {string} pathTemplate
     * @return {ValidationRule.PathSegment[]} {PathSegment[]}
     */
    getPathSegments(pathTemplate : string) : Array<ValidationRule.PathSegment> {
        let pathSegments : Array<ValidationRule.PathSegment> = <any>([]);
        if(pathTemplate == null || /* equals */(<any>((o1: any, o2: any) => { if(o1 && o1.equals) { return o1.equals(o2); } else { return o1 === o2; } })("",pathTemplate))) {
            return pathSegments;
        }
        let normalizedPath : string = pathTemplate;
        if(pathTemplate.lastIndexOf("/") === pathTemplate.length - 1) {
            normalizedPath = pathTemplate.substring(0, pathTemplate.length - 1);
        }
        let segId : number = 0;
        let matches : Array<string[]> = RegexCompat.findMatches(normalizedPath, ValidationRule.SEG_MATCH_REGEX);
        for(let index133=0; index133 < matches.length; index133++) {
            let mi = matches[index133];
            {
                let pathSegment : ValidationRule.PathSegment = new ValidationRule.PathSegment();
                pathSegment.segId = segId;
                pathSegment.prefix = mi[1];
                let fn : string = mi[3];
                if(fn != null && !/* isEmpty */(fn.trim().length === 0)) {
                    pathSegment.formalName = fn;
                    pathSegment.normalizedName = "__param__" + segId;
                }
                /* add */(pathSegments.push(pathSegment)>0);
                segId++;
            }
        }
        return pathSegments;
    }

    /**
     * Check if a value is either null or undefined.
     * @param {*} value
     * @return {boolean} {boolean}
     */
    isNullOrUndefined(value : any) : boolean {
        return NodeCompat.isNullOrUndefined(value);
    }

    /**
     * Returns true only if the given value is a valid URL.
     * @param {string} propertyValue
     * @return {boolean} {boolean}
     */
    isValidUrl(propertyValue : string) : boolean {
        return propertyValue.length < 2083 && RegexCompat.matches(propertyValue, ValidationRule.URL_MATCH_REGEX);
    }

    /**
     * Returns true only if the given value is a valid URL template.
     * @param {string} propertyValue
     * @return {boolean}
     */
    isValidUrlTemplate(propertyValue : string) : boolean {
        return true;
    }

    /**
     * Returns true only if the given value is valid GFM style markup.
     * @param {string} propertyValue
     * @return {boolean}
     */
    isValidGFM(propertyValue : string) : boolean {
        return true;
    }

    /**
     * Returns true only if the given value is valid CommonMark style markup.
     * @param {string} propertyValue
     * @return {boolean}
     */
    isValidCommonMark(propertyValue : string) : boolean {
        return true;
    }

    /**
     * Returns true only if the given value is a valid email address.
     * @param {string} propertyValue
     * @return {boolean}
     */
    isValidEmailAddress(propertyValue : string) : boolean {
        return RegexCompat.matches(propertyValue, ValidationRule.EMAIL_MATCH_REGEX);
    }

    /**
     * Returns true only if the given value is a valid mime-type.
     * @param {string[]} propertyValue
     * @return {boolean}
     */
    isValidMimeType(propertyValue : Array<string>) : boolean {
        for(let index134=0; index134 < propertyValue.length; index134++) {
            let v = propertyValue[index134];
            {
                if(!RegexCompat.matches(v, ValidationRule.MIME_TYPE_MATCH_REGEX)) {
                    return false;
                }
            }
        }
        return true;
    }

    /**
     * Returns true if the given value is an item in the enum list.
     * @param {string} value
     * @param {Array} items
     * @return {boolean}
     */
    isValidEnumItem(value : string, items : string[]) : boolean {
        for(let index135=0; index135 < items.length; index135++) {
            let item = items[index135];
            {
                if(item != null && /* equals */(<any>((o1: any, o2: any) => { if(o1 && o1.equals) { return o1.equals(o2); } else { return o1 === o2; } })(item,value))) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Returns true if the given value is valid according to the schema provided.
     * @param {*} value
     * @param {Node} node
     * @return {boolean}
     */
    isValidForType(value : any, node : Node) : boolean {
        return true;
    }

    /**
     * Returns true if the given status code is a valid HTTP response code.
     * @param {string} statusCode
     * @return {boolean}
     */
    isValidHttpCode(statusCode : string) : boolean {
        return ValidationRule.HTTP_STATUS_CODES.indexOf(statusCode) !== -1;
    }

    /**
     * Turns a list of args into a map suitable for use as template arguments.  The args must come
     * in pairs of "key, value".  For example:
     * 
     * map("key1", "value1", "key2", Boolean.TRUE);
     * 
     * That would return a {@link Map} two mappings.
     * @param {Array} args
     * @return {*}
     */
    map(...args : string[]) : any {
        if(args == null || args.length < 2) {
            return null;
        }
        let rval : any = <any>({});
        for(let idx : number = 0; idx < args.length; idx += 2) {{
            let key : string = args[idx].toString();
            let value : string = args[idx + 1];
            /* put */(rval[key] = value);
        };}
        return rval;
    }

    /**
     * Creates an array.
     * @param {Array} args
     * @return {Array}
     */
    array(...args : string[]) : string[] {
        return args;
    }

    /**
     * Returns true if the two values are equal.
     * @param {*} value1
     * @param {*} value2
     * @return {boolean}
     */
    equals(value1 : any, value2 : any) : boolean {
        return NodeCompat.equals(value1, value2);
    }
}
ValidationRule["__class"] = "io.apicurio.datamodels.core.validation.ValidationRule";
ValidationRule["__interfaces"] = ["io.apicurio.datamodels.core.visitors.IVisitor","io.apicurio.datamodels.asyncapi.v2.visitors.IAai20Visitor","io.apicurio.datamodels.openapi.v3.visitors.IOas30Visitor","io.apicurio.datamodels.asyncapi.visitors.IAaiVisitor","io.apicurio.datamodels.openapi.v2.visitors.IOas20Visitor","io.apicurio.datamodels.openapi.visitors.IOasVisitor"];



export namespace ValidationRule {

    /**
     * Type encapsulating information about a path segment.
     * Consumers of this type should not rely on normalizedName property which is only provided to weed out duplicates.
     * @class
     */
    export class PathSegment {
        public segId : number;

        public prefix : string;

        public formalName : string;

        public normalizedName : string;

        constructor() {
            if(this.segId===undefined) this.segId = 0;
            if(this.prefix===undefined) this.prefix = null;
            if(this.formalName===undefined) this.formalName = null;
            if(this.normalizedName===undefined) this.normalizedName = null;
        }
    }
    PathSegment["__class"] = "io.apicurio.datamodels.core.validation.ValidationRule.PathSegment";

}




import { ValidationRuleMetaData } from './ValidationRuleMetaData';


import { IValidationProblemReporter } from './IValidationProblemReporter';

